import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ExportRegistrationsModal } from '@/components/organizer/export-registrations-modal';
import { InscriptionDetailModal } from '@/components/organizer/inscription-detail-modal';
import { OrganizerTeamRegistrationModal } from '@/components/organizer/organizer-team-registration-modal';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { requestRefundApi } from '@/lib/api/paymentsApi';
import {
  createTeamCheckoutSessionApi,
  disableTeamApi,
  getTeamsApi,
  updateTeamRegistrationStatusApi,
} from '@/lib/api/teamsApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { PAY_STATUS_CONFIG, REG_STATUS_CONFIG, getTeamPaymentStatusConfig } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getInitials } from '@shared';
import { CompetitionStatus, PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Eye,
  Search,
  ShieldCheck,
  Trophy,
  UserPlus,
  Users,
  Users2,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';

// ─── Constants ──────────────────────────────────────────────────
const PAGE_SIZE = 20;

// ─── Badge config ────────────────────────────────────────────────
const VALID_REG_STATUSES = new Set<string>(Object.values(TeamRegistrationStatus));
const VALID_PAY_STATUSES = new Set<string>(Object.values(PaymentStatus));

// ─── Main Page ───────────────────────────────────────────────────
export default function OrganizerInscriptions() {
  const user = useAuthStore(s => s.user);
  const orgId = user?.organizationsAdmin[0]?.organizationId;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── URL params ───
  const rawCompId = searchParams.get('competitionId') ?? '';
  const selectedCompetitionId: number | '' = rawCompId && !isNaN(Number(rawCompId)) ? Number(rawCompId) : '';
  const rawRegStatus = searchParams.get('regStatus') ?? '';
  const regStatus: TeamRegistrationStatus | '' = VALID_REG_STATUSES.has(rawRegStatus)
    ? (rawRegStatus as TeamRegistrationStatus)
    : '';
  const rawPayStatus = searchParams.get('payStatus') ?? '';
  const payStatus: PaymentStatus | '' = VALID_PAY_STATUSES.has(rawPayStatus) ? (rawPayStatus as PaymentStatus) : '';
  const search = searchParams.get('search') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  // ─── Local input state for debounce ───
  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ─── Modal ───
  const [openTeamId, setOpenTeamId] = useState<number | null>(null);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  // ─── Competitions query ───
  const { data: competitionsData } = useQuery({
    queryKey: queryKeys.competitions.list({ organizationId: orgId, limit: 100 }),
    queryFn: () => getCompetitionsApi({ organizationId: orgId, limit: 100 }),
    enabled: !!orgId,
    select: res => res.data,
  });

  const competitions: Competition[] = useMemo(() => competitionsData ?? [], [competitionsData]);

  // Auto-select first active or first competition when no competitionId in URL
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (competitions.length > 0 && !hasInitialized.current && !selectedCompetitionId) {
      hasInitialized.current = true;
      const first =
        competitions.find(c =>
          [
            CompetitionStatus.REGISTRATION_OPEN,
            CompetitionStatus.REGISTRATION_CLOSED,
            CompetitionStatus.IN_PROGRESS,
          ].includes(c.status),
        ) ?? competitions[0];
      updateParams({ competitionId: String(first.id) });
    }
    if (competitions.length > 0) {
      hasInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitions]);

  // ─── Teams query ───
  const teamsParams = {
    competitionId: selectedCompetitionId ? Number(selectedCompetitionId) : undefined,
    ...(search && { search }),
    ...(regStatus && { registrationStatus: regStatus }),
    ...(payStatus && { paymentStatus: payStatus }),
    page,
    limit: PAGE_SIZE,
  };

  const {
    data: teamsData,
    isLoading: teamsLoading,
    isFetching: teamsFetching,
  } = useQuery({
    queryKey: queryKeys.teams.list(teamsParams),
    queryFn: () => getTeamsApi(teamsParams),
    enabled: !!selectedCompetitionId,
    placeholderData: keepPreviousData,
  });

  const teams = teamsData?.data ?? [];
  const totalTeams = teamsData?.meta?.total ?? 0;
  const totalPages = teamsData?.meta?.totalPages ?? 1;
  const from = totalTeams === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalTeams);

  // ─── Request refund mutation (D3 — décision 2026-05-22) ───
  // Crée une RefundRequest "PENDING" pour un paiement COMPLETED. Le backend
  // notifie tous les SUPER_ADMIN (in-app + email SMTP). L'exécution du refund
  // Stripe reste manuelle via /payments/:id/refund (SUPER_ADMIN uniquement).
  const requestRefundMutation = useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: number; reason: string }) =>
      requestRefundApi(paymentId, { reason }),
    onSuccess: () => {
      toast.success('Demande de remboursement envoyée aux SUPER_ADMIN');
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all() });
    },
    onError: () => {
      toast.error("Impossible d'envoyer la demande de remboursement");
    },
  });

  // ─── Status update mutation ───
  // Routage matrice §1.4 :
  //   CANCELLED → DELETE /teams/:id (soft cancel + libère la place)
  //   sinon     → PATCH /teams/:id/registration-status (workflow ORG_ADMIN)
  // Wrapper `async` pour unifier les types de retour (Team vs { message: string })
  // qui ne sont de toute façon pas consommés par onSuccess.
  const statusMutation = useMutation({
    mutationFn: async ({ teamId, status }: { teamId: number; status: TeamRegistrationStatus }) => {
      if (status === TeamRegistrationStatus.CANCELLED) {
        return disableTeamApi(teamId);
      }
      await updateTeamRegistrationStatusApi(teamId, status);
      return null;
    },
    onSuccess: result => {
      if (result?.refundRequestCreated) {
        toast.success('Inscription annulée — demande de remboursement créée');
      } else {
        toast.success('Statut mis à jour');
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all() });
    },
    onError: () => {
      toast.error('Impossible de mettre à jour le statut');
    },
  });

  // ─── CSV export (Demande client #5 — modale de filtres) ───
  const [exportOpen, setExportOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const checkoutMutation = useMutation({
    mutationFn: ({ teamId, competitionId }: { teamId: number; competitionId: number }) => {
      const returnUrl = `${window.location.origin}/organizer/inscriptions?competitionId=${competitionId}`;
      return createTeamCheckoutSessionApi(teamId, `${returnUrl}&payment=success`, `${returnUrl}&payment=cancel`);
    },
    onSuccess: ({ checkoutUrl }) => {
      if (!checkoutUrl) {
        toast.error('Stripe n’a pas renvoyé de lien de paiement');
        return;
      }
      window.location.assign(checkoutUrl);
    },
    onError: () => toast.error('Impossible de préparer le paiement Stripe'),
  });

  // ─── Handlers ───
  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value || undefined, page: undefined });
    }, 350);
  }

  function handleCompetitionChange(id: number | '') {
    updateParams({
      competitionId: id ? String(id) : undefined,
      page: undefined,
      search: undefined,
      regStatus: undefined,
      payStatus: undefined,
    });
    setSearchInput('');
    setExportOpen(false);
    setRegistrationOpen(false);
  }

  function handleRegStatusChange(value: TeamRegistrationStatus | '') {
    updateParams({ regStatus: value || undefined, page: undefined });
  }

  function handlePayStatusChange(value: PaymentStatus | '') {
    updateParams({ payStatus: value || undefined, page: undefined });
  }

  function handlePageChange(p: number) {
    updateParams({ page: p === 1 ? undefined : String(p) });
  }

  // ─── Aggregate stats from competitions list ───
  const totalCompetitions = competitions.length;
  const totalInscriptions = competitions.reduce((sum, c) => sum + c.currentTeams, 0);
  const openCompetitions = competitions.filter(c => c.status === CompetitionStatus.REGISTRATION_OPEN).length;
  const finishedCompetitions = competitions.filter(c => c.status === CompetitionStatus.FINISHED).length;

  const selectedCompetition = competitions.find(c => c.id === selectedCompetitionId);

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Inscriptions'
          subtitle='Gérez les inscriptions à vos concours'
          actions={
            <div className='flex items-center gap-2'>
              <button
                onClick={() => setRegistrationOpen(true)}
                disabled={
                  !selectedCompetition ||
                  selectedCompetition.status !== CompetitionStatus.REGISTRATION_OPEN ||
                  !selectedCompetition.playersPerTeam
                }
                className='flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
              >
                <UserPlus className='h-4 w-4' /> Inscrire des joueurs
              </button>
              <button
                onClick={() => setExportOpen(true)}
                disabled={!selectedCompetitionId}
                className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Download className='w-4 h-4 text-slate-600' />
                <span className='text-sm font-semibold text-slate-700'>Exporter CSV</span>
              </button>
            </div>
          }
        />

        {/* ─── Stats ── */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <StatCard
            icon={Calendar}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Concours organisés'
            value={String(totalCompetitions)}
            subtitle='Tous statuts confondus'
          />
          <StatCard
            icon={Users2}
            iconBg='bg-indigo-100'
            iconColor='text-indigo-600'
            label='Équipes inscrites'
            value={String(totalInscriptions)}
            subtitle='Toutes compétitions'
          />
          <StatCard
            icon={CheckCircle}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Inscriptions ouvertes'
            value={String(openCompetitions)}
            subtitle="Concours en cours d'inscription"
          />
          <StatCard
            icon={Trophy}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='Concours terminés'
            value={String(finishedCompetitions)}
            subtitle='Résultats disponibles'
          />
        </div>

        {/* ─── Filters ── */}
        <div className='flex flex-wrap items-center gap-3 mb-6'>
          {/* Competition selector */}
          <select
            value={selectedCompetitionId}
            onChange={e => handleCompetitionChange(e.target.value ? Number(e.target.value) : '')}
            className='px-4 py-2.5 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white min-w-[240px]'
          >
            <option value=''>— Sélectionner un concours —</option>
            {competitions.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.currentTeams} équipe{c.currentTeams !== 1 ? 's' : ''})
              </option>
            ))}
          </select>

          {/* Search */}
          <div className='relative flex-1 min-w-[180px] max-w-xs'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder='Rechercher une équipe…'
              className='w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm'
            />
          </div>

          {/* Registration status */}
          <select
            value={regStatus}
            onChange={e => handleRegStatusChange(e.target.value as TeamRegistrationStatus | '')}
            className='px-4 py-2.5 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white'
          >
            <option value=''>Tous les statuts</option>
            {Object.entries(REG_STATUS_CONFIG).map(([value, cfg]) => (
              <option key={value} value={value}>
                {cfg.label}
              </option>
            ))}
          </select>

          {/* Payment status */}
          <select
            value={payStatus}
            onChange={e => handlePayStatusChange(e.target.value as PaymentStatus | '')}
            className='px-4 py-2.5 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white'
          >
            <option value=''>Tous les paiements</option>
            {Object.entries(PAY_STATUS_CONFIG).map(([value, cfg]) => (
              <option key={value} value={value}>
                {cfg.label}
              </option>
            ))}
          </select>
        </div>

        {/* ─── Table ── */}
        <div className='bg-white rounded-2xl border border-slate-200'>
          {/* Competition context banner */}
          {selectedCompetition && (
            <div className='flex items-center justify-between px-6 py-3 bg-slate-50 rounded-t-2xl border-b border-slate-100'>
              <div className='flex items-center gap-2 text-sm min-w-0'>
                <Calendar className='w-4 h-4 text-slate-400 shrink-0' />
                <span className='font-semibold text-slate-700 truncate'>{selectedCompetition.name}</span>
                <span className='text-slate-300 shrink-0'>·</span>
                <span className='text-slate-500 shrink-0'>
                  {selectedCompetition.date ? formatDateAbbreviatedFr(selectedCompetition.date) : '—'}
                </span>
                <span className='text-slate-300 shrink-0'>·</span>
                <span className='text-slate-500 truncate'>{selectedCompetition.venue ?? selectedCompetition.city}</span>
              </div>
              <div className='flex items-center gap-2 shrink-0 ml-4'>
                <span className='text-sm text-slate-600'>
                  <span className='font-semibold'>{selectedCompetition.currentTeams}</span>
                  {selectedCompetition.maxTeams != null && ` / ${selectedCompetition.maxTeams}`} équipe
                  {selectedCompetition.currentTeams !== 1 ? 's' : ''}
                </span>
                {teamsFetching && !teamsLoading && <span className='text-xs text-slate-400'>Actualisation…</span>}
              </div>
            </div>
          )}

          {/* Table header */}
          <div className='grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
            <div className='col-span-2'>Équipe</div>
            <div className='col-span-2'>Inscription</div>
            <div className='col-span-2'>Paiement</div>
            <div className='col-span-2'>Capitaine</div>
            <div className='col-span-2'>Déclaration licence</div>
            <div className='col-span-1 text-center'>Joueurs</div>
            <div className='col-span-1 text-right'>Actions</div>
          </div>

          {/* Body */}
          {!selectedCompetitionId ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <Users2 className='w-12 h-12 opacity-30' />
              <p className='text-sm font-medium'>Sélectionnez un concours pour voir les inscriptions</p>
            </div>
          ) : teamsLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50'>
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
              </div>
            ))
          ) : teams.length === 0 ? (
            <div className='py-16 text-center text-sm text-slate-400'>
              Aucune équipe inscrite pour ce concours
              {(search || regStatus || payStatus) && ' avec ces filtres'}
            </div>
          ) : (
            teams.map(team => {
              const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
              const payCfg = getTeamPaymentStatusConfig(team, selectedCompetition);
              const playerCount = team.players?.length ?? 0;
              const sportingCaptain = team.registeredByOrganizer
                ? team.players?.find(p => p.playerOrder === 1)
                : undefined;
              const canPayPersonalAdvance =
                team.registeredByOrganizer &&
                team.captainId === user?.id &&
                team.registrationStatus === TeamRegistrationStatus.PENDING &&
                team.paymentStatus !== PaymentStatus.COMPLETED &&
                Number(selectedCompetition?.price ?? 0) > 0;
              const licensePlayers = team.players?.filter(p => p.licenseNumber?.trim()) ?? [];
              const hasLicenseDeclarationScope = licensePlayers.length > 0;
              const allLicensesDeclared =
                hasLicenseDeclarationScope && licensePlayers.every(p => !!p.licenseAutoDeclaredAt);

              return (
                <div
                  key={team.id}
                  className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50 items-center hover:bg-slate-50 transition'
                >
                  {/* Team name */}
                  <div className='col-span-2 min-w-0'>
                    <p className='font-semibold text-slate-900 text-sm truncate'>{team.name}</p>
                    <p className='text-xs text-slate-400 truncate font-mono'>{team.uid}</p>
                  </div>

                  {/* Registration status */}
                  <div className='col-span-2'>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${regCfg.bg} ${regCfg.text}`}
                    >
                      {regCfg.label}
                    </span>
                  </div>

                  {/* Payment status */}
                  <div className='col-span-2'>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${payCfg.bg} ${payCfg.text}`}
                    >
                      {payCfg.label}
                    </span>
                    {payCfg.isFreeCompetition ? (
                      <p className='text-xs text-slate-400 mt-0.5'>Aucun paiement requis</p>
                    ) : (
                      team.paidAmount != null &&
                      team.paidAmount > 0 && (
                        <p className='text-xs text-slate-400 mt-0.5'>{(team.paidAmount / 100).toFixed(2)} €</p>
                      )
                    )}
                  </div>

                  {/* Captain */}
                  <div className='col-span-2 min-w-0'>
                    {sportingCaptain || team.captain ? (
                      <div className='flex items-center gap-2'>
                        <div className='w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0'>
                          <span className='text-xs font-bold text-indigo-700'>
                            {getInitials(
                              sportingCaptain?.firstName ?? team.captain?.firstName,
                              sportingCaptain?.lastName ?? team.captain?.lastName,
                            )}
                          </span>
                        </div>
                        <div className='min-w-0'>
                          <p className='text-xs font-medium text-slate-700 truncate'>
                            {sportingCaptain?.firstName ?? team.captain?.firstName}{' '}
                            {sportingCaptain?.lastName ?? team.captain?.lastName}
                          </p>
                          {team.registeredByOrganizer && (
                            <p className='truncate text-xs text-blue-600'>
                              Inscrite par {team.captain?.firstName} {team.captain?.lastName}
                            </p>
                          )}
                          {team.captainPhone && <p className='text-xs text-slate-400 truncate'>{team.captainPhone}</p>}
                        </div>
                      </div>
                    ) : (
                      <span className='text-xs text-slate-300'>—</span>
                    )}
                  </div>

                  {/* Licence auto-declaration */}
                  <div className='col-span-2'>
                    {hasLicenseDeclarationScope ? (
                      allLicensesDeclared ? (
                        <span className='inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700'>
                          <ShieldCheck className='h-3 w-3' />
                          Auto-déclarée
                        </span>
                      ) : (
                        <span className='inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500'>
                          Déclaration non disponible
                        </span>
                      )
                    ) : (
                      <span className='text-xs text-slate-400'>Sans objet</span>
                    )}
                  </div>

                  {/* Players count */}
                  <div className='col-span-1 flex items-center justify-center gap-1 text-sm text-slate-600'>
                    <Users className='w-3.5 h-3.5 text-slate-400' />
                    {playerCount > 0 ? playerCount : <span className='text-slate-300'>—</span>}
                  </div>

                  {/* Actions */}
                  <div className='col-span-1 flex items-center justify-end gap-1'>
                    {canPayPersonalAdvance && (
                      <button
                        onClick={() => checkoutMutation.mutate({ teamId: team.id, competitionId: team.competitionId })}
                        disabled={checkoutMutation.isPending}
                        aria-label={`Payer personnellement l'équipe ${team.name}`}
                        title='Payer cette équipe avec mon compte'
                        className='rounded-lg p-2 text-blue-600 hover:bg-blue-50 disabled:opacity-40'
                      >
                        <CreditCard className='h-4 w-4' />
                      </button>
                    )}
                    <button
                      onClick={() => setOpenTeamId(team.id)}
                      aria-label={`Voir le détail de l'équipe ${team.name}`}
                      title='Voir le détail'
                      className='p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition'
                    >
                      <Eye className='w-4 h-4' />
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {/* Pagination */}
          {selectedCompetitionId && totalTeams > 0 && (
            <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
              <p className='text-sm text-slate-500'>
                Affichage de <span className='font-semibold text-slate-700'>{from}</span> à{' '}
                <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
                <span className='font-semibold text-slate-700'>{totalTeams.toLocaleString('fr-FR')}</span> équipes
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    aria-label='Page précédente'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p as number)}
                        aria-current={p === page ? 'page' : undefined}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    aria-label='Page suivante'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Team Detail Modal */}
      <InscriptionDetailModal
        teamId={openTeamId}
        competition={selectedCompetition}
        onClose={() => setOpenTeamId(null)}
        onStatusUpdate={(teamId, status) => statusMutation.mutate({ teamId, status })}
        updating={statusMutation.isPending}
        onRequestRefund={(paymentId, reason) => requestRefundMutation.mutate({ paymentId, reason })}
        requestingRefund={requestRefundMutation.isPending}
      />

      {registrationOpen && selectedCompetition && orgId && (
        <OrganizerTeamRegistrationModal
          key={selectedCompetition.id}
          competition={selectedCompetition}
          organizationId={orgId}
          onClose={() => setRegistrationOpen(false)}
          onCreated={() => {
            setRegistrationOpen(false);
            void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all() });
            void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
          }}
        />
      )}

      {exportOpen && (
        <ExportRegistrationsModal
          key={`${selectedCompetition?.id ?? 'none'}-${String(selectedCompetition?.playersPerTeam)}`}
          competitionId={selectedCompetition?.id ?? null}
          competitionAlias={selectedCompetition?.alias ?? null}
          playersPerTeam={selectedCompetition?.playersPerTeam}
          initialRegStatus={regStatus}
          initialPayStatus={payStatus}
          onClose={() => setExportOpen(false)}
        />
      )}
    </DashboardLayout>
  );
}
